import { DateTime } from "luxon"
import { isEmpty } from "lodash-es"
import { Box } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { BookingConfig, DynamicConfigKey, DynamicMainFormValues } from "../../../createOrder/components/form/formTypes"
import { TimelineCalendarEventTimeRange } from "../../../createOrder/components/resource/calendar/fullCalendarTypes"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { ResourceItem, SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { FormikSubmitButton } from "../../../forms/Formik"
import { MeetingDetails } from "./MeetingDetails"
import { ResourcesInfo } from "./ResourcesInfo"

const useStyles = makeStyles()(() => ({
    container: {
        display: "grid",
        gridTemplateColumns: "5fr 4fr",
        gap: "3.5rem",
        width: "100%",
        maxWidth: "60rem",
    },
    buttonWrap: {
        textAlign: "center",
        paddingTop: "1rem",
    },
    button: {
        minWidth: "15rem",
    },
}))

type MeetingDetailsFormPartProps = Readonly<{
    config: BookingConfig
    handleClick: (isShowParticipants: boolean) => void
    resourcesList: ResourceItem[]
    timeRange?: TimelineCalendarEventTimeRange
    handleClearResourceIds: () => void
    user: SelectedMainUserState
}>

export function MeetingDetailsFormPart({
    config,
    handleClick,
    resourcesList,
    timeRange,
    handleClearResourceIds,
    user,
}: MeetingDetailsFormPartProps) {
    const { classes } = useStyles()

    const { values, submitForm, errors } = useFormikContext<DynamicMainFormValues>()

    // const { resources: matchingResources } = findResourcesWithMessaging(values["resourceIds"], resourcesList || [])
    const matchingResources =
        resourcesList.filter((resource) => values["resourceIds"].includes(resource.id.toString())) || []

    const minTime = timeRange ? DateTime.fromISO(timeRange.start) : null
    const maxTime = timeRange ? DateTime.fromISO(timeRange.end) : null

    const startTime = DateTime.fromISO(values["startDateTime"])
    const endTime = DateTime.fromISO(values["endDateTime"])

    const isTimeInvalid = timeRange
        ? startTime < minTime! ||
          startTime >= maxTime! ||
          endTime <= startTime.plus({ minutes: 5 }) ||
          endTime > maxTime!
        : false

    const handleConfirmFirstStep = async () => {
        const hasBlockingErrors = Object.entries(errors).some(([key, value]) => {
            return key !== DynamicConfigKey.ADD_PARTICIPANTS_FORM && !!value && !isEmpty(value)
        })

        if (hasBlockingErrors) {
            submitForm()
            return
        }

        handleClick(!!values[DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE])
    }

    return (
        <>
            <Box className={classes.container}>
                <MeetingDetails resourcesList={matchingResources} timeRange={timeRange} user={user} config={config} />
                <ResourcesInfo
                    config={config}
                    user={user}
                    resourcesList={matchingResources}
                    // hasMessagingEnabled={hasMessagingEnabled}
                    handleClearResourceIds={handleClearResourceIds}
                />
            </Box>
            <Box className={classes.buttonWrap}>
                <LocalizedStrict id="create-order-confirm-booking-dialog-confirm-button">
                    <FormikSubmitButton
                        fullWidth={false}
                        updateDefaultClasses={classes.button}
                        onClick={() => handleConfirmFirstStep()}
                        parentDisabled={isTimeInvalid}
                    >
                        Confirm
                    </FormikSubmitButton>
                </LocalizedStrict>
            </Box>
        </>
    )
}
